import React, { useEffect, useRef, useState, useMemo, useCallback, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import { updateStructureTemplate, getStructureTemplate, deleteStructureTemplate, createStructureTemplate, checkTemplateStatus, resetTemplateCreation, reanalyzeStructureTemplate, shareTemplate } from '../../store/actions/TemplateActions';
import { useHistory } from 'react-router-dom';
import { useFormik, FieldArray, FormikProvider } from 'formik';
import * as Yup from 'yup';
import { PiPlusLight, PiTrashLight, PiCopyLight, PiPencilSimpleLight, PiCheckLight, PiXLight, PiCaretDownLight, PiCaretRightLight, PiCircleLight, PiChatTeardropLight, PiStackSimpleLight, PiQuotesLight, PiUserLight, PiUserSound, PiTextAlignLeftLight, PiArticleLight, PiDotsNineLight, PiTextAlignJustifyLight, PiDotsThreeVerticalLight, PiUsersThreeLight, PiArrowsClockwiseLight } from 'react-icons/pi';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import Badge from '../shared/TailwindComponents/Badge';
import FileUploader from '../shared/TailwindComponents/FileUploader';
import { DndProvider,DropTargetMonitor,useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { motion, AnimatePresence, Variants } from 'framer-motion';
import Button from '../shared/TailwindComponents/Button';
import Input from '../shared/TailwindComponents/Input';
import Dropdown from '../shared/TailwindComponents/Dropdown';
import { showModal, hideModal } from '../../store/actions/modalActions';
import { showSidePanel } from '../../store/actions/sidePanelActions';
import ProfileImage from '../Header/ProfileImage';
import CollaboratorBadge from '../shared/TailwindComponents/CollaboratorsBadge';
import SSECreationProgress from '../shared/SSECreationProgress';
import DetailFrame from '../shared/Frames/DetailFrame';
import { Menu, Transition } from '@headlessui/react';
import clsx from 'clsx';
import CollaboratorSelector, { Collaborator } from '../shared/CollaboratorSelector';
import { BaseForm, BaseFormFieldConfig, BaseFormSectionConfig, OrgWideToggle } from '../shared/FormComponents';
import { FormSchema, FormField } from '../../utils/validation/form-schema';
import { getMultiHubSocketService } from '../../utils/MultiHubSocketService';
import { TEMPLATE_CREATE_COMPLETED, TEMPLATE_CREATE_FAILED, TEMPLATE_CREATE_STEP, TEMPLATE_CREATE_STEPS } from '../../store/types';
import { useLicenseGate } from '../../hooks/useLicenseGate';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { FeatureTour, createStructureFormTourSteps, structureDetailTourSteps } from '../shared/FeatureTour';

interface StructureFormProps {
  templateId?: string;
}

interface OutlineItem {
  level: number;
  title: string;
  content_prompt: string;
  example_language: string;
  fallbacks: string[];
  subsections: OutlineItem[];
  number?: string;
}

interface StructureFormValues {
  TemplateName: string;
  DraftType: string;
  OwnerType: string;
  SharedWith: { UserID: string; Name: string; Email: string; ProfilePicture: string | null; Initials: string; Access?: string }[];  // Typed as array of objects
  Description: string;
  Structure: { outline: OutlineItem[] };
  Tone: string;
  WritingStyle: string;
  document?: File | null;
  CreatedAt?: string;
  UpdatedAt?: string;
  Owner: { UserID: string; Name: string; Email: string; ProfilePicture: string | null; Initials: string };  // Add Owner as object
}

const subsectionVariants: Variants = {
  hidden: { opacity: 0, height: 0 },
  visible: { opacity: 1, height: 'auto', transition: { duration: 0.2 } },
  exit: { opacity: 0, height: 0, transition: { duration: 0.2 } },
};

const SkeletonSection = () => (
  <div className="bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 p-6 shadow-sm space-y-6 animate-fadePulse">
    <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
    <div className="space-y-2">
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
      <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded"></div>
    </div>
    <div className="space-y-2">
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
      <div className="h-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
    </div>
    <div className="space-y-2">
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
      <div className="space-y-2">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
      </div>
    </div>
  </div>
);

const DraggableSection = ({ item, index, formik, setEditingSectionIndex, sectionRefs, editingSectionIndex, editingSubsectionKey, setEditingSubsectionKey }: { item: OutlineItem & { number: string }; index: number; formik: any; setEditingSectionIndex: (index: number | null) => void; sectionRefs: React.MutableRefObject<{ [key: string]: HTMLDivElement | null }>; editingSectionIndex: any; editingSubsectionKey: string | null; setEditingSubsectionKey: (key: string | null) => void }) => {
  const { t } = useTranslation();
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'section',
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }));

  const [, drop] = useDrop({
    accept: 'section',
    hover: (draggedItem: { index: number }, monitor: DropTargetMonitor) => {
      if (draggedItem.index !== index) {
        const newOutline = [...formik.values.Structure.outline];
        const [moved] = newOutline.splice(draggedItem.index, 1);
        newOutline.splice(index, 0, moved);
        formik.setFieldValue('Structure.outline', newOutline);
        draggedItem.index = index;
      }
    },
  });

  const ref = useRef<HTMLDivElement | null>(null);
  const contentPromptRef = useRef<HTMLTextAreaElement | null>(null);
  const exampleLanguageRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (ref.current) {
      drag(ref.current);
      drop(ref.current);
      sectionRefs.current[item.number] = ref.current;
    }
  }, [drag, drop, item.number, sectionRefs]);

  useEffect(() => {
    const autoResize = (textarea: HTMLTextAreaElement | null) => {
      if (textarea) {
        textarea.style.height = 'auto';
        textarea.style.height = `${textarea.scrollHeight}px`;
      }
    };

    autoResize(contentPromptRef.current);
    autoResize(exampleLanguageRef.current);
  }, [item.content_prompt, item.example_language]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: isDragging ? 0.5 : 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="cursor-move"
    >
      <div className='bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden'>
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-750">
          {editingSectionIndex === index ? (
            <div className="flex items-center gap-2 flex-1">
              <Input
                name={`Structure.outline.${index}.title`}
                value={item.title}
                onChange={formik.handleChange}
                className="flex-1 text-lg font-medium"
                type="text"
              />
              <button onClick={() => setEditingSectionIndex(null)} className="text-green-500 hover:text-green-600 p-1"><PiCheckLight size={20} /></button>
              <button onClick={() => setEditingSectionIndex(null)} className="text-red-500 hover:text-red-600 p-1"><PiXLight size={20} /></button>
            </div>
          ) : (
            <div className="flex items-center gap-3 flex-1 group cursor-pointer" onClick={() => setEditingSectionIndex(index)}>
              <span className="text-sm font-mono text-gray-400 bg-gray-200 dark:bg-gray-600 px-2 py-0.5 rounded">{item.number}</span>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white group-hover:text-primary-500 transition-colors">{item.title || t('Untitled Section')}</h3>
              <PiPencilSimpleLight className="text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" size={16} />
            </div>
          )}
          <PiDotsNineLight size={20} className='text-gray-400 hover:text-gray-600 cursor-grab' />
        </div>
        <div className='p-5 space-y-6'>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-2">
              <PiChatTeardropLight size={16} className="text-primary-500" /> {t('Content Prompt')}
            </label>
            <textarea
              ref={contentPromptRef}
              name={`Structure.outline.${index}.content_prompt`}
              value={item.content_prompt}
              onChange={formik.handleChange}
              placeholder={t('Describe what content should go in this section...')}
              className="w-full p-3 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors text-sm resize-y min-h-[80px] text-gray-800 dark:text-white"
              rows={4}
              onInput={(e) => {
                const target = e.target as HTMLTextAreaElement;
                target.style.height = 'auto';
                target.style.height = `${target.scrollHeight}px`;
              }}
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-2">
              <PiQuotesLight size={16} className="text-blue-500" /> {t('Example Language')}
            </label>
            <textarea
              ref={exampleLanguageRef}
              name={`Structure.outline.${index}.example_language`}
              value={item.example_language}
              onChange={formik.handleChange}
              placeholder={t('Add example phrasing or language samples...')}
              className="w-full p-3 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors text-sm text-gray-800 dark:text-white resize-y min-h-[80px]"
              rows={4}
              onInput={(e) => {
                const target = e.target as HTMLTextAreaElement;
                target.style.height = 'auto';
                target.style.height = `${target.scrollHeight}px`;
              }}
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-2">
              <PiCircleLight size={16} className="text-yellow-500" /> {t('Fallbacks')}
            </label>
            <FieldArray name={`Structure.outline.${index}.fallbacks`}>
              {({ remove: removeFallback, push: pushFallback }) => (
                <div className="space-y-3">
                  <AnimatePresence>
                    {item.fallbacks.map((fallback, fbIndex) => (
                      <motion.div
                        key={fbIndex}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        className="flex items-center gap-2"
                      >
                        <span className="text-xs text-gray-400 w-6">{fbIndex + 1}.</span>
                        <input
                          name={`Structure.outline.${index}.fallbacks.${fbIndex}`}
                          value={fallback}
                          onChange={formik.handleChange}
                          placeholder={t('Alternative phrasing...')}
                          className="flex-1 p-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          type="text"
                        />
                        <button onClick={() => removeFallback(fbIndex)} className="text-gray-400 hover:text-red-500 p-1 transition-colors"><PiTrashLight size={16} /></button>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                  <Button onClick={() => pushFallback('')} variant='outline' size='small'>
                    <PiPlusLight size={16} /> {t('Add Fallback')}
                  </Button>
                </div>
              )}
            </FieldArray>
          </div>
          <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-3">
              <PiStackSimpleLight size={16} className="text-purple-500" /> {t('Subsections')} 
              <span className="text-xs text-gray-400 font-normal">({item.subsections?.length || 0})</span>
            </label>
            <FieldArray name={`Structure.outline.${index}.subsections`}>
              {({ remove: removeSub, push: pushSub }) => (
                <div className="space-y-4">
                  {item.subsections.map((sub, subIndex) => (
                    <div key={subIndex} ref={(el) => { sectionRefs.current[sub.number!] = el; }} className="p-4 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2 flex-1">
                          <span className="text-xs font-mono text-gray-400 bg-gray-200 dark:bg-gray-600 px-1.5 py-0.5 rounded">{sub.number}</span>
                          {editingSubsectionKey === `${index}-${subIndex}` ? (
                            <div className="flex items-center gap-2 flex-1">
                              <input
                                name={`Structure.outline.${index}.subsections.${subIndex}.title`}
                                value={sub.title}
                                onChange={formik.handleChange}
                                className="flex-1 p-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                type="text"
                                placeholder={t('Subsection title...')}
                                autoFocus
                              />
                              <button onClick={() => setEditingSubsectionKey(null)} className="text-green-500 hover:text-green-600 p-1"><PiCheckLight size={16} /></button>
                              <button onClick={() => setEditingSubsectionKey(null)} className="text-red-500 hover:text-red-600 p-1"><PiXLight size={16} /></button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 flex-1 cursor-pointer group" onClick={() => setEditingSubsectionKey(`${index}-${subIndex}`)}>
                              <h4 className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-primary-500">{sub.title || t('Untitled Subsection')}</h4>
                              <PiPencilSimpleLight className="text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" size={14} />
                            </div>
                          )}
                        </div>
                        <button onClick={() => removeSub(subIndex)} className="text-gray-400 hover:text-red-500 transition-colors">
                          <PiTrashLight size={16} />
                        </button>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <label className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1 mb-1">
                            <PiChatTeardropLight size={12} /> {t('Content Prompt')}
                          </label>
                          <textarea
                            name={`Structure.outline.${index}.subsections.${subIndex}.content_prompt`}
                            value={sub.content_prompt}
                            onChange={formik.handleChange}
                            placeholder={t('Describe subsection content...')}
                            className="w-full p-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y min-h-[60px]"
                            rows={3}
                            onInput={(e) => {
                              const target = e.target as HTMLTextAreaElement;
                              target.style.height = 'auto';
                              target.style.height = `${target.scrollHeight}px`;
                            }}
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1 mb-1">
                            <PiQuotesLight size={12} /> {t('Example Language')}
                          </label>
                          <textarea
                            name={`Structure.outline.${index}.subsections.${subIndex}.example_language`}
                            value={sub.example_language}
                            onChange={formik.handleChange}
                            placeholder={t('Example phrasing...')}
                            className="w-full p-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y min-h-[60px]"
                            rows={3}
                            onInput={(e) => {
                              const target = e.target as HTMLTextAreaElement;
                              target.style.height = 'auto';
                              target.style.height = `${target.scrollHeight}px`;
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  <Button onClick={() => pushSub({ level: 2, title: '', content_prompt: '', example_language: '', fallbacks: [], subsections: [] })} variant='outline' size='small'>
                    <PiPlusLight size={16} /> {t('Add Subsection')}
                  </Button>
                </div>
              )}
            </FieldArray>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const StructureForm: React.FC<StructureFormProps> = ({ templateId }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { checkSectionGate, checkAiFeatureGate, hasAiFeatureAccess } = useLicenseGate();
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const [isFetching, setIsFetching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchedRef = useRef(false);
  const userNavigatedAwayRef = useRef(false); // Track if user navigated away during creation
  const [newTemplateId, setNewTemplateId] = useState<string | undefined>(templateId);
  const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editingSectionIndex, setEditingSectionIndex] = useState<number | null>(null);
  const [editingSubsectionKey, setEditingSubsectionKey] = useState<string | null>(null);
  const [openSections, setOpenSections] = useState<Set<number>>(new Set());
  const {isConnecting, connected, steps, complete, ok, summary} = useSelector((state: RootState) => state.templates)
  const [isCreating, setIsCreating] = useState(false)
  const [isOrgWide, setIsOrgWide] = useState(false);
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const pubsubCleanupRef = useRef<(() => void) | null>(null);

  // ─── Tours ─────────────────────────────────────────────────────────
  const { isRunning: isCreateTourRunning, handleJoyrideCallback: handleCreateTourCallback } = useFeatureTour({
    tourId: 'create-structure-form',
    steps: createStructureFormTourSteps,
    readyToShow: !newTemplateId && !isFetching && !isCreating && !isSaving && !isConnecting,
    autoStartDelay: 600,
  });

  const { isRunning: isDetailTourRunning, handleJoyrideCallback: handleDetailTourCallback } = useFeatureTour({
    tourId: 'structure-detail',
    steps: structureDetailTourSteps,
    readyToShow: !!newTemplateId && !isFetching && !isCreating && !isSaving && !isConnecting && !error,
    autoStartDelay: 600,
  });

  // Sync newTemplateId with templateId prop when it changes (e.g., after redirect)
  useEffect(() => {
    if (templateId !== newTemplateId) {
      setNewTemplateId(templateId);
      fetchedRef.current = false; // Reset fetch flag to allow re-fetching
    }
  }, [templateId, newTemplateId]);

  // Cleanup: reset template creation state on unmount
  useEffect(() => {
    return () => {
      if (pubsubCleanupRef.current) {
        pubsubCleanupRef.current();
      }
      dispatch(resetTemplateCreation());
    };
  }, [dispatch]);

  const draftTypeOptions = [
    { value: '', label: t('Select Document Type') },
    { value: 'any', label: t('All Types') },
    { value: 'agreement', label: t('Agreement') },
    { value: 'brief', label: t('Brief') },
    { value: 'complaint', label: t('Complaint') },
    { value: 'contract', label: t('Contract') },
    { value: 'correspondence', label: t('Correspondence') },
    { value: 'court-filing', label: t('Court Filing') },
    { value: 'discovery', label: t('Discovery') },
    { value: 'engagement-letter', label: t('Engagement Letter') },
    { value: 'legal-opinion', label: t('Legal Opinion') },
    { value: 'memo', label: t('Memo') },
    { value: 'motion', label: t('Motion') },
    { value: 'nda', label: t('NDA') },
    { value: 'pleading', label: t('Pleading') },
    { value: 'policy', label: t('Policy') },
    { value: 'power-of-attorney', label: t('Power of Attorney') },
    { value: 'proposal', label: t('Proposal') },
    { value: 'report', label: t('Report') },
    { value: 'settlement', label: t('Settlement') },
    { value: 'will-trust', label: t('Will / Trust') },
    { value: 'other', label: t('Other') },
  ];

  // ── BaseForm sections & schema for creation mode ──
  const createFormSections: BaseFormSectionConfig[] = useMemo(() => [
    {
      dataTourId: 'structure-form-name',
      fields: [
        {
          name: 'TemplateName',
          type: 'input',
          label: t('Template Name'),
          placeholder: t('Enter template name'),
          required: true,
          gridSize: 'full',
        } as BaseFormFieldConfig,
      ],
    },
    {
      fields: [
        {
          name: 'DraftType',
          type: 'dropdown',
          label: t('Document Type'),
          placeholder: t('Select Document Type'),
          required: true,
          options: draftTypeOptions,
          customProps: { fullWidth: true },
          gridSize: 'full',
        } as BaseFormFieldConfig,
      ],
    },
    {
      dataTourId: 'structure-form-sharing',
      title: t('Sharing'),
      description: t('Control who can access this template'),
      fields: [
        {
          name: 'is_org_wide',
          type: 'custom',
          label: '',
          gridSize: 'full',
          customComponent: OrgWideToggle,
          customProps: { title: t('Organization-wide template') },
        } as BaseFormFieldConfig,
        ...(!isOrgWide ? [{
          name: 'collaborators',
          type: 'collaboratorSelector',
          label: t('Access'),
          gridSize: 'full',
          customProps: {
            ownerLabel: t('You are the template owner'),
            showOwnerSection: true,
          },
        } as BaseFormFieldConfig] : []),
      ],
    },
    {
      dataTourId: 'structure-form-upload',
      title: t('Upload Document'),
      description: t('Upload a document to extract the template structure from.'),
      fields: [
        {
          name: 'document',
          type: 'fileUploader',
          label: t('Document'),
          required: true,
          gridSize: 'full',
          maxFiles: 1,
          allowedExtensions: ['docx', 'pdf'],
        } as BaseFormFieldConfig,
      ],
    },
  ], [t, draftTypeOptions, isOrgWide]);

  const createFormSchema = useMemo(() => {
    return new FormSchema('createStructureTemplate', [
      new FormField('TemplateName', 'text', true, t('Template Name')),
      new FormField('DraftType', 'text', true, t('Document Type')),
      new FormField('is_org_wide', 'text', false, t('Organization-wide')),
      new FormField('collaborators', 'text', false, t('Access')),
      new FormField('document', 'text', true, t('At least one document'), {
        customValidator: (value: any) => {
          if (!value || (Array.isArray(value) && value.length === 0)) {
            return { isValid: false, error: t('Upload a document to extract the template from') };
          }
          return { isValid: true };
        },
      }),
    ]);
  }, [t]);

  // Step ID mapping from AI Engine step names to frontend step IDs
  const STEP_NAME_TO_ID: Record<string, string> = {
    'Uploading document': 'upload',
    'Validating format': 'validate',
    'Extracting content': 'extract',
    'Analyzing structure': 'analyze',
    'Generating template': 'generate',
  };

  const connectToPubSub = useCallback(async (
    correlationId: string,
    templateUuid: string,
    onComplete?: () => void,
  ) => {
    const hub = 'ai_processing';
    const group = `template_${correlationId}`;
    const socketService = getMultiHubSocketService();

    try {
      const isConnected = await socketService.connectToHub(hub);
      if (!isConnected) {
        console.warn('[StructureForm] Failed to connect to PubSub hub');
        return;
      }

      // Join the template processing group
      socketService.emit(hub, 'join', { group });

      // Listen for all events
      const handleEvent = (eventData: any) => {
        // Only handle events for our correlation ID
        if (eventData?.correlationId !== correlationId && eventData?.correlation_id !== correlationId) {
          return;
        }

        const subType = eventData?.data?.sub_type || eventData?.sub_type || '';
        const stepName = eventData?.data?.step_name || eventData?.stepName || '';
        const message = eventData?.message || '';
        const stepId = STEP_NAME_TO_ID[stepName] || stepName;

        if (subType === 'step_started') {
          dispatch({ type: TEMPLATE_CREATE_STEP, payload: { id: stepId, status: 'in_progress', message, name: stepName } });
        } else if (subType === 'step_progress') {
          dispatch({ type: TEMPLATE_CREATE_STEP, payload: { id: stepId, status: 'in_progress', message, name: stepName } });
        } else if (subType === 'step_completed') {
          dispatch({ type: TEMPLATE_CREATE_STEP, payload: { id: stepId, status: 'completed', message, name: stepName } });
        } else if (subType === 'step_failed') {
          dispatch({ type: TEMPLATE_CREATE_STEP, payload: { id: stepId, status: 'failed', message, name: stepName } });
        } else if (subType === 'completed') {
          // Full pipeline complete — clean up PubSub, reset state
          if (pubsubCleanupRef.current) {
            pubsubCleanupRef.current();
            pubsubCleanupRef.current = null;
          }

          dispatch(resetTemplateCreation());
          setIsCreating(false);
          setIsSaving(false);

          if (onComplete) {
            // Custom completion handler (e.g., re-analysis refetch)
            onComplete();
          } else {
            // Default: show success + navigate (creation flow)
            dispatch(showFeedbackModal({
              showModal: true,
              message: t('Template created successfully!'),
              modalType: 'success',
              duration: 3000,
            }));
            history.push(`/templates/${templateUuid}?type=structure`);
          }
        } else if (subType === 'failed') {
          const error = eventData?.data?.error || message || 'Processing failed';

          // Clean up PubSub
          if (pubsubCleanupRef.current) {
            pubsubCleanupRef.current();
            pubsubCleanupRef.current = null;
          }

          dispatch({ type: TEMPLATE_CREATE_FAILED, payload: { error } });
          dispatch(showFeedbackModal({
            showModal: true,
            message: error,
            modalType: 'error',
            duration: 4000,
          }));
          setIsCreating(false);
          setIsSaving(false);
        }
      };

      socketService.on(hub, '*', handleEvent);

      // Store cleanup function
      pubsubCleanupRef.current = () => {
        socketService.off(hub, '*', handleEvent);
        socketService.emit(hub, 'leave', { group });
      };

    } catch (err) {
      console.error('[StructureForm] PubSub connection error:', err);
    }
  }, [dispatch, history, t]);

  const handleCreateSubmit = useCallback(async (formData: Record<string, any>) => {
    if (!checkSectionGate('templates')) return;
    if (!checkAiFeatureGate('template_ai')) return;
    setIsSaving(true);
    setIsCreating(true);

    const orgWide = !!formData.is_org_wide;
    const sharedWithIds = orgWide ? [] : (formData.collaborators || []).map((c: any) => c.collaborator_uuid || c.user_id).filter(Boolean);
    const data = {
      TemplateName: formData.TemplateName || '',
      DraftType: formData.DraftType,
      OwnerType: orgWide ? 'firm-wide' : 'personal',
      SharedWith: sharedWithIds,
    };
    const files: { [key: string]: File } = {};
    const uploadedFiles = formData.document;
    if (Array.isArray(uploadedFiles) && uploadedFiles.length > 0) {
      files.document = uploadedFiles[0];
    } else if (uploadedFiles instanceof File) {
      files.document = uploadedFiles;
    }

    try {
      userNavigatedAwayRef.current = false;
      const response = await dispatch(createStructureTemplate(data, files) as any);

      if (response?.status === 201 && response?.data?.uuid) {
        const { uuid, correlationId } = response.data;

        // Connect to PubSub for live progress updates from AI Engine
        if (correlationId) {
          await connectToPubSub(correlationId, uuid);
        } else {
          // No PubSub available — mark as completed immediately
          dispatch({ type: TEMPLATE_CREATE_COMPLETED, payload: { ok: true } });
          setIsCreating(false);
          setIsSaving(false);
          history.push(`/templates/${uuid}?type=structure`);
        }
        return;
      }
      setIsCreating(false);
      setIsSaving(false);
    } catch (err) {
      if (!err) return;
      dispatch(showFeedbackModal({
        modalType: 'error',
        message: t('Failed to save template.'),
        showModal: true,
        duration: 3000,
      }));
      setIsCreating(false);
      setIsSaving(false);
    }
  }, [dispatch, history, t, connectToPubSub]);

  const createValidationSchema = Yup.object({
    DraftType: Yup.string().required(t('Document Type is required')),
    OwnerType: Yup.string().optional(),
    TemplateName: Yup.string().required(t('Template Name is required')),
    document: Yup.mixed().required(t('Document is required')),
  });

  const editValidationSchema = Yup.object({
    TemplateName: Yup.string().required(t('Name is required')),
    DraftType: Yup.string().required(t('Document Type is required')),
    OwnerType: Yup.string().optional(),
    Description: Yup.string(),
    Structure: Yup.object({
      outline: Yup.array().min(1, t('At least one section is required')).of(
        Yup.object({
          title: Yup.string().required(t('Section title is required')),
          content_prompt: Yup.string(),
          example_language: Yup.string(),
          fallbacks: Yup.array().of(Yup.string()),
          subsections: Yup.array().of(
            Yup.object({
              title: Yup.string().required(t('Subsection title is required')),
              content_prompt: Yup.string(),
              example_language: Yup.string(),
              fallbacks: Yup.array().of(Yup.string()),
            })
          ),
        })
      ),
    }),
    Tone: Yup.string(),
    WritingStyle: Yup.string(),
  });

  const formik = useFormik<StructureFormValues>({
    initialValues: {
      TemplateName: '',
      DraftType: '',
      OwnerType: 'personal',
      SharedWith: [],
      Description: '',
      Structure: { outline: [] },
      Tone: '',
      WritingStyle: '',
      document: null,
      CreatedAt: '',
      UpdatedAt: '',
      Owner: { 
        Name: userInfo.FullName || '', 
        Email: userInfo.Email || '', 
        UserID: userInfo.UserUID || userInfo.uuid_encrypted || '', 
        Initials: userInfo.FullName ? userInfo.FullName.split(' ').map((n: string) => n[0]).join('').substring(0, 2).toUpperCase() : 'U', 
        ProfilePicture: userInfo.ProfilePicture || ''
      }
    },
    validationSchema: newTemplateId ? editValidationSchema : createValidationSchema,
    validateOnChange: false,
    validateOnBlur: true,
    onSubmit: async (values) => {
      if (!checkSectionGate('templates')) return;
      if (!checkAiFeatureGate('template_ai')) return;
      setIsSaving(true);
      setIsCreating(true)
      // Convert collaborators to SharedWith format (array of user IDs)
      const sharedWithIds = isOrgWide ? [] : collaborators.map(c => c.collaborator_uuid || c.user_id).filter(Boolean);
      const data = {
        TemplateName: values.TemplateName,
        DraftType: values.DraftType,
        OwnerType: isOrgWide ? 'firm-wide' : (values.OwnerType || 'personal'),
        SharedWith: sharedWithIds,
        ...(newTemplateId ? {
          Description: values.Description,
          Structure: values.Structure,
          Tone: values.Tone,
          WritingStyle: values.WritingStyle,
        } : {}),
      };
      const files: { [key: string]: File } = {};
      if (values.document) {
        files.document = values.document;
      }
      try {
        let response;
        if (newTemplateId) {
          response = await dispatch(updateStructureTemplate(newTemplateId, data, files));
        } else {
          userNavigatedAwayRef.current = false;
          
          response = await dispatch(createStructureTemplate(data, files) as any);
          
          if (response?.status === 201 && response?.data?.uuid) {
            const { uuid, correlationId } = response.data;
            
            // Connect to PubSub for live progress updates
            if (correlationId) {
              await connectToPubSub(correlationId, uuid);
            } else {
              dispatch({ type: TEMPLATE_CREATE_COMPLETED, payload: { ok: true } });
              setIsCreating(false);
              setIsSaving(false);
              history.push(`/templates/${uuid}?type=structure`);
            }
            return;
          }
        }
        dispatch(showFeedbackModal({
          modalType: 'success',
          message: newTemplateId ? t('Template updated!') : t('Template created!'),
          showModal: true,
          duration: 3000
        }));
      } catch (err) {
        if(!err) return 
        dispatch(showFeedbackModal({
          modalType: 'error',
          message: t('Failed to save template.'),
          showModal: true,
          duration: 3000
        }));
      } finally {
        setIsCreating(false)
        setIsSaving(false)
      }
    },
    enableReinitialize: true,
  });

const mapAiToForm = (template: any) => {
  const aiStructure = template.Structure || {};
  let sharedWith = template.SharedWith || [];
  if (typeof sharedWith === 'string') {
    try {
      sharedWith = JSON.parse(sharedWith);
    } catch (e) {}
  }
  
  // Build owner from template or fallback to current user
  const owner = template.Owner && template.Owner.UserID 
    ? template.Owner 
    : { 
        UserID: userInfo.UserUID || userInfo.uuid_encrypted || '', 
        Name: userInfo.FullName || '', 
        Email: userInfo.Email || '', 
        ProfilePicture: userInfo.ProfilePicture || null, 
        Initials: userInfo.FullName ? userInfo.FullName.split(' ').map((n: string) => n[0]).join('').substring(0, 2).toUpperCase() : 'U' 
      };
  
  const ownerType = template.OwnerType || 'personal';
  setIsOrgWide(ownerType === 'firm-wide');
  formik.setValues({
    TemplateName: template.TemplateName || aiStructure.suggested_name || '',
    DraftType: template.DraftType || '',
    OwnerType: ownerType,
    SharedWith: sharedWith,
    Description: template.Description || aiStructure.description || '',
    Structure: { outline: Array.isArray(aiStructure.outline) ? aiStructure.outline : [] },  
    Tone: template.Tone || aiStructure.tone || '',
    WritingStyle: template.WritingStyle || aiStructure.writing_style || '',
    document: null,
    CreatedAt: template.CreatedAt,
    UpdatedAt: template.UpdatedAt,
    Owner: owner,
  });
};

 const assignNumbers = (outline: OutlineItem[] | undefined) => {  
    if (!outline || !Array.isArray(outline)) {
        return [];  
    }
    let sectionNum = 1;
    return outline.map((item) => {
        const numberedItem = { ...item, number: `${sectionNum}.0` };
        let subNum = 1;
        numberedItem.subsections = (numberedItem.subsections || []).map((sub) => ({ ...sub, number: `${sectionNum}.${subNum++}` }));  
        sectionNum++;
        return numberedItem;
    });
    };

  const numberedOutline = assignNumbers(formik.values.Structure?.outline);

  useEffect(() => {
    setOpenSections(new Set(numberedOutline.map((_, index) => index)));
  }, [numberedOutline.length]);

  const toggleSection = (index: number) => {
    const newOpenSections = new Set(openSections);
    if (newOpenSections.has(index)) {
      newOpenSections.delete(index);
    } else {
      newOpenSections.add(index);
    }
    setOpenSections(newOpenSections);
  };

  const formatOutline = (outline: (OutlineItem & { number: string })[]): string => {
    let result = '';
    const formatItem = (item: OutlineItem & { number: string }, indent: string = '') => {
      result += `${indent}${item.number} ${item.title}\n`;
      if (item.content_prompt) {
        result += `${indent}Content Prompt: ${item.content_prompt}\n`;
      }
      if (item.example_language) {
        result += `${indent}Example Language: ${item.example_language}\n`;
      }
      if (item.fallbacks?.length > 0) {
        result += `${indent}Fallbacks:\n`;
        item.fallbacks.forEach((fallback, fbIndex) => {
          result += `${indent}  ${fbIndex + 1}: ${fallback}\n`;
        });
      }
      if (item.subsections?.length > 0) {
        item.subsections.forEach((sub: any) => formatItem(sub, indent + '  '));
      }
      result += '\n';
    };
    outline.forEach(item => formatItem(item));
    return result.trim();
  };

  const copyOutline = () => {
    navigator.clipboard.writeText(formatOutline(numberedOutline));
    dispatch(showFeedbackModal({ modalType: 'success', message: t('Outline copied to clipboard!'), showModal: true, duration: 2000 }));
  };

    const handleDelete = async () => {
    dispatch(showModal({
        showModal: true,
        type: "confirmation",
        isDest: true,
        icon: <PiTrashLight className='w-6 h-6 text-red-600' />,
        message: t("Are you sure you want to delete this template?"),
        subMsg: t("This action cannot be undone."),
        onConfirm: async () => {
        dispatch(hideModal());
        try {
            const resp = await dispatch(deleteStructureTemplate(newTemplateId!) as any);
            // deleteStructureTemplate returns response.data on 200, throws on error
            if (resp) {
              history.push('/templates?type=structure');
            }
        } catch (err: any) {
            // Action already shows error feedback via handleApiError
            console.error('[StructureForm] Delete failed:', err);
        }
        },
        onCancel: () => dispatch(hideModal()),
        duration: 3000,
        inputData: "Yes, Delete",
    }));
    };

  const handleAccessControl = () => {
    // Document templates use a single bulk-share endpoint (POST /templates/share),
    // not per-row CRUD. Each adapter callback computes the new SharedWith list and
    // sends the full payload, so the unified sidepanel can still drive add/remove/access.
    const buildPayload = (sharedWith: any[]) => ({
      TemplateID: newTemplateId,
      SharedWith: sharedWith,
    });

    const currentSharedWith = (): any[] => formik.values.SharedWith || [];

    const findUser = (userUuid: string): any | null => {
      // The CollaboratorSelector pushes a Collaborator with user_id; we mirror it
      // back into the legacy SharedWith shape used by /templates/share.
      const match = (formik.values.SharedWith || []).find(
        (s: any) => String(s.UserID) === String(userUuid)
      );
      return match || { UserID: userUuid, Name: '', Email: '', Initials: '', ProfilePicture: null };
    };

    dispatch(showSidePanel({
      type: 'manageResourceCollaborators',
      showSidePanel: true,
      width: '50%',
      message: t('Manage Access'),
      resourceUuid: newTemplateId || '',
      resourceType: 'template',
      collaborators: (formik.values.SharedWith || []).map((s: any) => ({
        user_id: s.UserID,
        collaborator_uuid: s.UserID,
        name: s.Name || '',
        email: s.Email || '',
        initials: s.Initials || '',
        profile_picture: s.ProfilePicture || null,
        access: s.Access || 'edit',
        role: 'collaborator',
      })),
      currentUserUuid: userInfo?.UserUID,
      canAdmin: true,
      addCollaborator: async (_uuid: string, userUuid: string, access: string) => {
        const base = findUser(userUuid);
        const next = [...currentSharedWith(), { ...base, UserID: userUuid, Access: access }];
        formik.setFieldValue('SharedWith', next);
        await dispatch(shareTemplate(buildPayload(next)) as any);
      },
      removeCollaborator: async (_uuid: string, collabUuid: string) => {
        const next = currentSharedWith().filter((s: any) => String(s.UserID) !== String(collabUuid));
        formik.setFieldValue('SharedWith', next);
        await dispatch(shareTemplate(buildPayload(next)) as any);
      },
      updateAccess: async (_uuid: string, collabUuid: string, access: string) => {
        const next = currentSharedWith().map((s: any) =>
          String(s.UserID) === String(collabUuid) ? { ...s, Access: access } : s
        );
        formik.setFieldValue('SharedWith', next);
        await dispatch(shareTemplate(buildPayload(next)) as any);
      },
      onUpdate: () => {},
    } as any));
  };

  const handleReanalyze = async () => {
    if (!newTemplateId) return;
    dispatch(showModal({
      showModal: true,
      type: 'confirmation',
      message: t('Re-analyze Template'),
      subMsg: t('The document will be re-processed by AI. Current outline and metadata will be replaced with freshly extracted data.'),
      icon: <PiArrowsClockwiseLight className="w-6 h-6 text-blue-600" />,
      onConfirm: async () => {
        dispatch(hideModal());
        setIsCreating(true);
        setIsSaving(true);
        try {
          const result = await dispatch(reanalyzeStructureTemplate(newTemplateId) as any);
          if (result?.processing?.correlationId) {
            await connectToPubSub(result.processing.correlationId, newTemplateId, async () => {
              // Show skeleton loader immediately so old data is never visible
              setIsFetching(true);
              try {
                const refreshed = await dispatch(getStructureTemplate(newTemplateId) as any);
                if (refreshed) {
                  mapAiToForm(refreshed);
                }
                dispatch(showFeedbackModal({
                  showModal: true,
                  message: t('Template re-analyzed successfully!'),
                  modalType: 'success',
                  duration: 3000,
                }));
              } catch (fetchErr) {
                console.error('[StructureForm] Failed to refetch after re-analysis:', fetchErr);
                fetchedRef.current = false;
                dispatch(showFeedbackModal({
                  showModal: true,
                  message: t('Re-analysis complete. Please refresh to see updates.'),
                  modalType: 'warning',
                  duration: 4000,
                }));
              } finally {
                setIsFetching(false);
              }
            });
          } else {
            setIsCreating(false);
            setIsSaving(false);
          }
        } catch (err) {
          setIsCreating(false);
          setIsSaving(false);
          dispatch(showFeedbackModal({
            modalType: 'error',
            message: t('Failed to start re-analysis.'),
            showModal: true,
            duration: 3000,
          }));
        }
      },
      onCancel: () => dispatch(hideModal()),
    }));
  };
  
  // const [showAccessControlModal, setShowAccessControlModal] = useState(false); // Replaced with side panel

  const handleEditField = (field: 'Description' | 'Tone' | 'WritingStyle', title: string, rows: number = 3, placeholder: string = '') => {
    dispatch(showModal({
      type: `edit${field}`,
      message: t(title),
      subMsg: t('Write and save the changes you need.'),
      icon: <PiPencilSimpleLight className='w-6 h-6 text-gray-600' />,
      inputData: formik.values[field],
      onConfirm: (newValue: string) => {
        formik.setFieldValue(field, newValue);
        dispatch(hideModal());
      },
      onCancel: () => dispatch(hideModal()),
      showModal: true,
      rows,
      placeholder,
    }));
  };

  useEffect(() => {
    if (newTemplateId && !fetchedRef.current) {
      fetchedRef.current = true;
      setIsFetching(true);
      dispatch(getStructureTemplate(newTemplateId))
        .then((template) => {
          if (template) {
            // Check if template is still being processed by AI Engine
            const templateStatus = template.status || template.Status;
            if (templateStatus === 'pending' || templateStatus === 'processing') {
              // Template is still processing - poll until complete
              setIsCreating(true);
              const pollInterval = setInterval(async () => {
                try {
                  const updated = await dispatch(checkTemplateStatus(newTemplateId));
                  const updatedStatus = updated?.status || updated?.Status;
                  if (updatedStatus === 'completed' || updatedStatus === 'failed') {
                    clearInterval(pollInterval);
                    if (updatedStatus === 'completed') {
                      const refreshed = await dispatch(getStructureTemplate(newTemplateId));
                      if (refreshed) {
                        mapAiToForm(refreshed);
                      }
                    }
                    setIsCreating(false);
                    setIsFetching(false);
                  }
                } catch (err) {
                  console.error('Poll status error:', err);
                  clearInterval(pollInterval);
                  setIsCreating(false);
                  setIsFetching(false);
                }
              }, 3000);
            } else {
              mapAiToForm(template);
              setIsFetching(false);
            }
          } else {
            setError(t('Template not found'));
            setIsFetching(false);
          }
        })
        .catch((err) => {
          console.error('Fetch error:', err);
          setError(t('Error loading template'));
          setIsFetching(false);
        });
    }
  }, [newTemplateId, dispatch, t]);

  const templateBreadcrumbs = [
    { name: t('Templates'), href: '/templates?type=structure', current: false },
    { name: formik.values.TemplateName || t('Create New Template'), href: newTemplateId ? `/templates/${newTemplateId}?type=structure` : '/templates?type=structure', current: true },
  ];

  const templateDropdownActions = [
    {
      label: t('Save'),
      onClick: () => formik.handleSubmit(),
      icon: <PiCheckLight className="h-4 w-4" />,
      disabled: isSaving || !hasAiFeatureAccess('template_ai'),
    },
    {
      label: t('Copy Outline'),
      onClick: copyOutline,
      icon: <PiCopyLight className="h-4 w-4" />,
    },
    {
      label: t('Access Control'),
      onClick: handleAccessControl,
      icon: <PiUsersThreeLight className="h-4 w-4" />,
    },
    {
      label: t('Re-analyze'),
      onClick: handleReanalyze,
      icon: <PiArrowsClockwiseLight className="h-4 w-4" />,
      disabled: !newTemplateId || isCreating,
    },
    {
      label: t('Delete'),
      onClick: handleDelete,
      icon: <PiTrashLight className="h-4 w-4 text-red-900" />,
      variant: 'destructive',
      disabled: !newTemplateId, 
    },
  ];

  const additionalHeaderElements = (
    <div className="flex items-center gap-3">
      {/* Collaborators with access - show owner + shared users */}
      <div className="flex items-center">
        {/* Owner - always show if we have any identifying info */}
        {(formik.values.Owner?.UserID || formik.values.Owner?.Name || formik.values.Owner?.Email) && (
          <div className="relative rounded-full">
            <CollaboratorBadge collaborator={{
              name: formik.values.Owner.Name || 'Owner',
              avatarURL: formik.values.Owner.ProfilePicture,
              initials: formik.values.Owner.Initials || formik.values.Owner.Name?.split(' ').map((n: string) => n[0]).join('').substring(0, 2).toUpperCase() || 'U',
              email: formik.values.Owner.Email,
              access: 'admin',
            }} />
          </div>
        )}
        {/* SharedWith users (up to 3 more) */}
        {Array.isArray(formik.values.SharedWith) && formik.values.SharedWith.filter(s => s.UserID || s.Initials || s.Email || s.Name).slice(0, 3).map((shared, index) => (
          <div key={index} className={clsx('relative rounded-full', 'ml-[-0.5rem]')}>
            <CollaboratorBadge collaborator={{
              name: shared.Name || '',
              avatarURL: shared.ProfilePicture,
              initials: shared.Initials || shared.Name?.split(' ').map((n: string) => n[0]).join('').substring(0, 2).toUpperCase() || '?',
              email: shared.Email,
              access: shared.Access || 'edit',
            }} />
          </div>
        ))}
        {/* Show overflow count if more than 3 shared users */}
        {formik.values.SharedWith.filter(s => s.UserID || s.Initials || s.Email || s.Name).length > 3 && (
          <div className="relative ml-[-0.5rem]" title={formik.values.SharedWith.filter(s => s.UserID || s.Initials || s.Email || s.Name).slice(3).map(s => `${s.Name} (${s.Access || 'edit'})`).join(', ')}>
            <div className="h-7 w-7 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs text-gray-700 dark:text-white font-medium">
              +{formik.values.SharedWith.filter(s => s.UserID || s.Initials || s.Email || s.Name).length - 3}
            </div>
          </div>
        )}
      </div>

      {/* Save button */}
      <Button size='small' onClick={async () => {
        const errors = await formik.validateForm();
        if (Object.keys(errors).length > 0) {
          // Show validation error message
          const flatErrors: string[] = [];
          const extractErrors = (obj: any, prefix = ''): void => {
            for (const key in obj) {
              if (typeof obj[key] === 'string') {
                flatErrors.push(obj[key]);
              } else if (typeof obj[key] === 'object') {
                extractErrors(obj[key], `${prefix}${key}.`);
              }
            }
          };
          extractErrors(errors);
          dispatch(showFeedbackModal({
            modalType: 'error',
            message: flatErrors[0] || t('Please fix validation errors before saving.'),
            showModal: true,
            duration: 4000
          }));
          return;
        }
        formik.handleSubmit();
      }} variant="primary">
        {t('Save')}
      </Button>

      {/* Three-dot menu - simple styling */}
      <Menu as="div" className="relative inline-block text-left z-50">
        <Menu.Button as={Button} size="small" variant="outline" className="p-1">
          <PiDotsThreeVerticalLight className="h-4 w-4" />
        </Menu.Button>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="absolute right-0 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
            <div className="px-1 py-1">
              {templateDropdownActions.map((action, index) => (
                <Menu.Item key={index}>
                  {({ active }) => (
                    <button
                      className={clsx(
                        'group flex w-full items-center rounded-md px-2 py-2 text-sm gap-2',
                        active ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300',
                        action.variant === 'destructive' && 'text-red-600 dark:text-red-400'
                      )}
                      onClick={action.onClick}
                      disabled={action.disabled}
                    >
                      {action.icon}
                      {action.label}
                    </button>
                  )}
                </Menu.Item>
              ))}
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
    </div>
  );


  if (isFetching) {
    return (
      <div className="flex h-screen overflow-hidden bg-gray-50 dark:bg-gray-900">
        <aside className="w-1/4 relative max-w-96 pl-6 border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <div className="flex gap-2 items-center pr-2 animate-fadePulse">
            <div className="w-4 h-4 bg-gray-200 rounded"></div>
            <div className="flex-1 h-6 bg-gray-200 rounded"></div>
          </div>
          <ul className="space-y-4 pr-6 border-t border-gray-200 pt-2">
            {[...Array(5)].map((_, i) => (
              <li key={i} className="animate-fadePulse">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="ml-4 space-y-2 mt-2">
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              </li>
            ))}
          </ul>
        </aside>

        <main className="flex-1 p-8 overflow-y-auto bg-gray-50 dark:bg-gray-900 custom-scrollbar-main">
          <div className="max-w-3xl mx-auto space-y-8">
            {[...Array(3)].map((_, i) => (
              <SkeletonSection key={i} />
            ))}
          </div>
        </main>

        <aside className="w-64 p-6 border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 space-y-6 animate-fadePulse">
          <div className="space-y-4">
            <div className="h-4 bg-gray-200 rounded w-1/4"></div>
            <div className="h-10 bg-gray-200 rounded"></div>
          </div>
          <div className="space-y-4">
            <div className="h-4 bg-gray-200 rounded w-1/4"></div>
            <div className="h-16 bg-gray-200 rounded"></div>
          </div>
          <div className="space-y-4">
            <div className="h-4 bg-gray-200 rounded w-1/4"></div>
            <div className="space-y-2">
              <div className="h-8 bg-gray-200 rounded"></div>
              <div className="h-8 bg-gray-200 rounded"></div>
            </div>
          </div>
        </aside>
      </div>
    );
  }

  if (isCreating || isSaving || isConnecting) {
    const handleCancelTemplateCreation = () => {
      // Mark that user navigated away - PubSub completion should not redirect
      userNavigatedAwayRef.current = true;
      
      // Reset template creation state
      dispatch(resetTemplateCreation());
      setIsCreating(false);
      setIsSaving(false);
      
      // Navigate back to templates list
      history.push('/templates?type=structure');
    };

    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <SSECreationProgress
          title={t('Creating Template')}
          isConnecting={isConnecting}
          steps={steps}
          connected={connected}
          complete={complete}
          ok={ok}
          summary={summary}
          onCancel={handleCancelTemplateCreation}
          type='template'
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center space-y-4 bg-gray-50 dark:bg-gray-900">
        <Badge color="red">{error}</Badge>
        <Button variant="primary" size="small" onClick={() => history.push('/templates?type=structure')}>
          {t('Back to Templates')}
        </Button>
      </div>
    );
  }

if (!newTemplateId) {
    return (
      <>
      <BaseForm
        title={t('Create New Template')}
        description={t('Fill out this form to add your template.')}
        sections={createFormSections}
        schema={createFormSchema}
        initialValues={{
          TemplateName: '',
          DraftType: '',
          is_org_wide: false,
          collaborators: [],
          document: [],
        }}
        onSubmit={handleCreateSubmit}
        onCancel={() => history.goBack()}
        onValuesChange={(values) => {
          if (values.is_org_wide !== isOrgWide) {
            setIsOrgWide(!!values.is_org_wide);
          }
        }}
        submitLabel={t('Create Template')}
        cancelLabel={t('Cancel')}
        isSubmitting={isSaving}
        hideLoadingOverlay={true}
        maxWidth="max-w-3xl"
        submitDisabled={!hasAiFeatureAccess('template_ai')}
        submitTooltip={t('Upgrade your plan to use AI Templates.')}
      />
      <FeatureTour
        steps={createStructureFormTourSteps}
        run={isCreateTourRunning}
        onCallback={handleCreateTourCallback}
        continuous
        showProgress
        showSkipButton
        disableOverlay
      />
      </>
    );
  }
  


  return (
    <DetailFrame
      breadcrumbs={templateBreadcrumbs}
      dropdownActions={[]}
      subscriptionActive={true}
      additionalHeaderElements={additionalHeaderElements}
      showBackButton={true}
      className="!flex-row flex-1 overflow-hidden bg-gray-50 dark:bg-gray-900 !px-0"
    >
    <FormikProvider value={formik}>
      <DndProvider backend={HTML5Backend}>
            <aside data-tour-id="structure-detail-sidebar" className="w-72 min-w-[200px] max-w-xs flex-shrink-0 relative border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            <div className="flex gap-2 items-center p-4 border-b border-gray-200 dark:border-gray-700">
                {editingField === 'templateName' ? (
                <div className="flex items-center flex-1 min-w-0">
                    <Input
                    name="TemplateName"
                    value={formik.values.TemplateName}
                    onChange={formik.handleChange}
                    onBlur={() => setEditingField(null)}
                    className="text-sm w-full max-w-full overflow-hidden"
                    type="text"
                    />
                    <PiPencilSimpleLight className="inline text-gray-400 flex-shrink-0 ml-2" size={16} />
                </div>
                ) : (
                <div onClick={() => setEditingField('templateName')} className="flex items-center justify-between flex-1 min-w-0 cursor-pointer group">
                    <h2 className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-primary-500 truncate">{formik.values.TemplateName || t('Untitled Template')}</h2>
                    <PiPencilSimpleLight className="inline text-gray-400 flex-shrink-0 ml-2 opacity-0 group-hover:opacity-100 transition-opacity" size={18} />
                </div>
                )}
            </div>
            <ul className="space-y-2 text-gray-600 p-4 dark:text-gray-300 overflow-y-auto custom-scrollbar-main h-[calc(100%-60px)]">
                <AnimatePresence>
                {numberedOutline.map((item, index) => (
                    <motion.li
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="text-sm"
                    >
                    <div className="flex items-center justify-between py-1 px-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                        <span className="cursor-pointer hover:text-primary-500 transition-colors truncate font-medium" onClick={() => sectionRefs.current[item.number]?.scrollIntoView({ behavior: 'smooth' })}>
                        <span className="text-gray-400 mr-2">{item.number}</span>{item.title}
                        </span>
                        {item.subsections.length !== 0 && (
                        <button onClick={() => toggleSection(index)} className="text-gray-400 hover:text-primary-500 p-1">
                            {openSections.has(index) ? <PiCaretDownLight size={14} /> : <PiCaretRightLight size={14} />}
                        </button>
                        )}
                    </div>
                    <AnimatePresence>
                        {openSections.has(index) && item.subsections.map((sub, subIndex) => (
                        <motion.div
                            key={subIndex}
                            variants={subsectionVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                            className="ml-6 py-1 text-xs text-gray-500 dark:text-gray-400 cursor-pointer hover:text-primary-500 transition-colors truncate"
                            onClick={() => sectionRefs.current[sub.number!]?.scrollIntoView({ behavior: 'smooth' })}
                        >
                            <span className="text-gray-400 mr-2">{sub.number}</span>{sub.title}
                        </motion.div>
                        ))}
                    </AnimatePresence>
                    </motion.li>
                ))}
                </AnimatePresence>
            </ul>
            </aside>

          <main data-tour-id="structure-detail-editor" className="flex-1 min-w-0 p-6 overflow-y-auto bg-gray-50 dark:bg-gray-900 custom-scrollbar-main">
            <div className="space-y-6">
              <AnimatePresence>
                {numberedOutline.map((item, index) => (
                  <DraggableSection
                    key={index}
                    item={item}
                    index={index}
                    formik={formik}
                    setEditingSectionIndex={setEditingSectionIndex}
                    sectionRefs={sectionRefs}
                    editingSectionIndex={editingSectionIndex}
                    editingSubsectionKey={editingSubsectionKey}
                    setEditingSubsectionKey={setEditingSubsectionKey}
                  />
                ))}
              </AnimatePresence>
              <button onClick={() => formik.setFieldValue('Structure.outline', [...formik.values.Structure.outline, { level: 1, title: '', content_prompt: '', example_language: '', fallbacks: [], subsections: [] }])} className="text-sm text-gray-500 hover:text-primary-500 transition-colors flex items-center gap-2 px-4 py-2 rounded-lg border border-dashed border-gray-300 hover:border-primary-400 w-full justify-center">
                <PiPlusLight size={16} /> {t('Add Section')}
              </button>
            </div>
          </main>

          <aside data-tour-id="structure-detail-metadata" className="w-64 min-w-[200px] flex-shrink-0 p-4 border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex flex-col overflow-hidden">
            <div className="flex flex-col flex-1 gap-3 min-h-0">
              <div className="flex-1 min-h-0 cursor-pointer p-3 bg-gray-50 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-lg transition-colors flex flex-col" onClick={() => handleEditField('Description', t('Edit Description'), 10, t('Enter description here...'))}>
                <div className="flex gap-2 items-center mb-2 flex-shrink-0">
                  <PiTextAlignLeftLight className="text-gray-400" size={14} />
                  <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">{t('Description')}</label>
                </div>
                <p className="text-xs text-gray-700 dark:text-gray-300 overflow-y-auto flex-1 custom-scrollbar-main">
                  {formik.values.Description || <span className="text-gray-400 italic">{t('Click to add description...')}</span>}
                </p>
              </div>
              <div className="flex-1 min-h-0 cursor-pointer p-3 bg-gray-50 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-lg transition-colors flex flex-col" onClick={() => handleEditField('Tone', t('Edit Tone'), 3, t('Enter tone here...'))}>
                <div className="flex gap-2 items-center mb-2 flex-shrink-0">
                  <PiUserSound className="text-gray-400" size={14} />
                  <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">{t('Tone')}</label>
                </div>
                <p className="text-xs text-gray-700 dark:text-gray-300 overflow-y-auto flex-1 custom-scrollbar-main">
                  {formik.values.Tone || <span className="text-gray-400 italic">{t('Click to set tone...')}</span>}
                </p>
              </div>
              <div className="flex-1 min-h-0 cursor-pointer p-3 bg-gray-50 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-lg transition-colors flex flex-col" onClick={() => handleEditField('WritingStyle', t('Edit Writing Style'), 3, t('Enter writing style here...'))}>
                <div className="flex gap-2 items-center mb-2 flex-shrink-0">
                  <PiArticleLight className="text-gray-400" size={14} />
                  <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">{t('Writing Style')}</label>
                </div>
                <p className="text-xs text-gray-700 dark:text-gray-300 overflow-y-auto flex-1 custom-scrollbar-main">
                  {formik.values.WritingStyle || <span className="text-gray-400 italic">{t('Click to set style...')}</span>}
                </p>
              </div>
            </div>
          </aside>
      </DndProvider>
    </FormikProvider>
    
    {/* Access Control Modal — replaced with side panel via handleAccessControl */}
    <FeatureTour
      steps={structureDetailTourSteps}
      run={isDetailTourRunning}
      onCallback={handleDetailTourCallback}
      continuous
      showProgress
      showSkipButton
      disableOverlay
    />
    </DetailFrame>
  );
};

export default StructureForm;
